// track/hooks/useDuplicateDetection.ts
import { useState, useEffect, useCallback } from 'react';
import { Transaction } from '../types';
import { hashCSVRow } from '../utils';

// The structure for a potential duplicate, including the new and existing transactions
export interface PotentialDuplicate {
  newTransaction: any;
  existingTransaction: Transaction;
  include: boolean; // Whether the user wants to import it anyway
  index: number;
}

export const useDuplicateDetection = (existingTransactions: Transaction[]) => {
  const [potentialDuplicates, setPotentialDuplicates] = useState<PotentialDuplicate[]>([]);
  const [nonDuplicateTransactions, setNonDuplicateTransactions] = useState<any[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transactionHashMap, setTransactionHashMap] = useState<Map<string, Transaction>>(new Map());

  // Pre-process existing transactions to create a hash-to-transaction map for quick lookups.
  useEffect(() => {
    const computeHashMap = async () => {
      const newMap = new Map<string, Transaction>();
      const promises = existingTransactions.map(async (t) => {
        // CORRECTED: Pass the array directly to the hashing function
        const rowAsArray = [t.date, t.description, String(t.amount)];
        const hash = await hashCSVRow(rowAsArray);
        
        if (!newMap.has(hash)) {
            newMap.set(hash, t);
        }
      });
      await Promise.all(promises);
      setTransactionHashMap(newMap);
    };

    computeHashMap();
  }, [existingTransactions]);

  const findDuplicates = useCallback(async (newTransactions: any[]) => {
    setIsProcessing(true);
    const duplicates: PotentialDuplicate[] = [];
    const nonDuplicates: any[] = [];

    const hashPromises = newTransactions.map(newTx => {
        // CORRECTED: Pass the array directly to the hashing function
        const rowAsArray = [newTx.date, newTx.description, String(newTx.amount)];
        return hashCSVRow(rowAsArray);
    });
    
    const newTxHashes = await Promise.all(hashPromises);

    for (let i = 0; i < newTransactions.length; i++) {
      const newTx = newTransactions[i];
      const newTxHash = newTxHashes[i];

      const potentialMatch = transactionHashMap.get(newTxHash);

      if (potentialMatch) {
        duplicates.push({
          newTransaction: newTx,
          existingTransaction: potentialMatch,
          include: false, // Default to not including duplicates
          index: i,
        });
      } else {
        nonDuplicates.push(newTx);
      }
    }

    setPotentialDuplicates(duplicates);
    setNonDuplicateTransactions(nonDuplicates);
    setIsProcessing(false);

    return { duplicates, nonDuplicates };
  }, [transactionHashMap]);

  const toggleDuplicateInclusion = (index: number) => {
    setPotentialDuplicates(prev =>
      prev.map((dup) =>
        dup.index === index ? { ...dup, include: !dup.include } : dup
      )
    );
  };

  const toggleAllDuplicates = (include: boolean) => {
    setPotentialDuplicates(prev =>
      prev.map(dup => ({ ...dup, include }))
    );
  };

  const reset = () => {
    setPotentialDuplicates([]);
    setNonDuplicateTransactions([]);
  };

  return {
    potentialDuplicates,
    nonDuplicateTransactions,
    isProcessing,
    findDuplicates,
    toggleDuplicateInclusion,
    toggleAllDuplicates,
    reset,
  };
};